import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Coins,
  ShieldCheck,
  Tag,
  Loader2,
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Gift,
  Sparkles,
  Plus,
  Minus,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { apiService } from '../services/api.service';
import { toast } from 'sonner';

interface PricingTier {
  min_quantity: number;
  max_quantity: number | null;
  price_per_token_cents: number;
}

interface EventTier {
  slug: string;
  name: string;
  max_lanes: number;
  max_days: number;
  token_cost: number;
}

interface TokenTransaction {
  id: string;
  type: string;
  tokens_amount: number;
  balance_after: number;
  price_per_token_cents: number | null;
  total_price_cents: number | null;
  event_id: string | null;
  event_name: string | null;
  tier_slug: string | null;
  description: string;
  created_at: string;
}

interface License {
  id: string;
  event_id: string | null;
  event_name: string | null;
  product_name: string;
  product_slug: string;
  max_lanes: number;
  max_days: number;
  source: string;
  status: string;
  tokens_consumed: number;
  final_price_cents: number;
  activated_at: string | null;
  created_at: string;
}

interface PriceInfo {
  quantity: number;
  price_per_token_cents: number;
  base_total_cents: number;
  stripe_connect_discount_cents: number;
  promo_discount_cents: number;
  total_discount_cents: number;
  final_price_cents: number;
  discounts: { type: string; label: string; amount_cents: number }[];
  next_tier_info: {
    tokens_needed: number;
    next_price_per_token_cents: number;
    savings_per_token_cents: number;
  } | null;
  promo_code_id: string | null;
}

const MyLicense: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [tokenBalance, setTokenBalance] = useState(0);
  const [welcomeTokensGranted, setWelcomeTokensGranted] = useState(false);
  const [stripeConnectActive, setStripeConnectActive] = useState(false);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [eventTiers, setEventTiers] = useState<EventTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Purchase success highlight
  const [highlightLatest, setHighlightLatest] = useState(false);

  // Purchase flow
  const [quantity, setQuantity] = useState(10);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<any>(null);
  const [priceInfo, setPriceInfo] = useState<PriceInfo | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [applyConnectDiscount, setApplyConnectDiscount] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [licensesRes, pricingRes] = await Promise.all([
        apiService.getMyLicenses(),
        apiService.getTokenPricing(),
      ]);
      setTokenBalance(licensesRes.token_balance || 0);
      setWelcomeTokensGranted(licensesRes.welcome_tokens_granted || false);
      setStripeConnectActive(licensesRes.stripe_connect_active || false);
      setLicenses(licensesRes.licenses || []);
      setTransactions(licensesRes.recent_transactions || []);
      setPricingTiers(pricingRes.pricing_tiers || []);
      setEventTiers(pricingRes.event_tiers || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('myLicense.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-open purchase dialog if ?action=buy (e.g. coming from Organizer)
  useEffect(() => {
    if (!loading && searchParams.get('action') === 'buy') {
      openPurchaseDialog();
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show success toast + highlight after Stripe payment return
  useEffect(() => {
    if (!loading && searchParams.get('purchase') === 'success') {
      toast.success(t('myLicense.purchaseSuccess'));
      setHighlightLatest(true);
      searchParams.delete('purchase');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      // Fade out highlight after 3s
      const timer = setTimeout(() => setHighlightLatest(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate price when quantity changes in dialog
  const calculatePrice = useCallback(async (qty: number, code?: string, connectDiscount?: boolean) => {
    if (qty <= 0) return;
    setPriceLoading(true);
    try {
      const res = await apiService.calculateTokenPrice(qty, code, connectDiscount);
      setPriceInfo(res);
    } catch {
      setPriceInfo(null);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // Debounced price calculation
  useEffect(() => {
    if (!purchaseDialogOpen || quantity <= 0) return;
    const timer = setTimeout(() => {
      calculatePrice(quantity, promoResult?.valid ? promoCode : undefined, applyConnectDiscount);
    }, 300);
    return () => clearTimeout(timer);
  }, [quantity, promoResult, promoCode, purchaseDialogOpen, calculatePrice, applyConnectDiscount]);

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const res = await apiService.validatePromoCode(promoCode, 'license');
      setPromoResult(res);
      if (!res.valid) {
        toast.error(t('myLicense.promoInvalid'));
      } else {
        toast.success(t('myLicense.promoApplied'));
      }
    } catch {
      setPromoResult({ valid: false });
      toast.error(t('myLicense.promoError'));
    } finally {
      setPromoValidating(false);
    }
  };

  const handlePurchase = async () => {
    if (quantity <= 0) return;
    setPurchasing(true);
    try {
      const res = await apiService.purchaseTokens(
        quantity,
        promoResult?.valid ? promoCode : undefined,
        applyConnectDiscount,
      );

      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else if (res.free) {
        toast.success(res.message || t('myLicense.tokensCredited'));
        setPurchaseDialogOpen(false);
        loadData();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('myLicense.purchaseError'));
    } finally {
      setPurchasing(false);
    }
  };

  const openPurchaseDialog = (presetQty?: number) => {
    setQuantity(presetQty || 10);
    setPromoCode('');
    setPromoResult(null);
    setPriceInfo(null);
    setApplyConnectDiscount(false);
    setPurchaseDialogOpen(true);
  };

  const formatPrice = (cents: number) => `${(cents / 100).toFixed(0)}€`;
  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const txnIcon = (type: string) => {
    switch (type) {
      case 'purchase': return <ArrowUp size={14} className="text-success" />;
      case 'welcome_bonus': return <Gift size={14} className="text-muted-foreground" />;
      case 'consume': return <ArrowDown size={14} className="text-muted-foreground" />;
      case 'gift': return <Gift size={14} className="text-muted-foreground" />;
      case 'refund': return <ArrowUp size={14} className="text-muted-foreground" />;
      default: return <Coins size={14} />;
    }
  };

  const txnLabel = (type: string) => {
    switch (type) {
      case 'purchase': return t('myLicense.txnPurchase');
      case 'welcome_bonus': return t('myLicense.txnWelcome');
      case 'consume': return t('myLicense.txnConsume');
      case 'gift': return t('myLicense.txnGift');
      case 'refund': return t('myLicense.txnRefund');
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="animate-spin mr-2" size={20} />
        {t('common.loading')}
      </div>
    );
  }

  const activeLicenses = licenses.filter(l => l.status === 'active');

  // Find which pricing tier user is currently in
  const currentTier = pricingTiers.find(t =>
    quantity >= t.min_quantity && (t.max_quantity === null || quantity <= t.max_quantity)
  );

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <Coins size={22} />
          {t('myLicense.title')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t('myLicense.subtitle')}</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Token Balance + Quick Buy */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Balance Card */}
        <Card className="sm:col-span-1">
          <CardContent className="pt-6 text-center">
            <Coins size={32} className="mx-auto mb-2 text-muted-foreground" />
            <div className="text-4xl font-bold">{tokenBalance}</div>
            <p className="text-sm text-muted-foreground mt-1">{t('myLicense.tokensAvailable')}</p>
            {!welcomeTokensGranted && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <Gift size={12} />
                {t('myLicense.welcomeTokensPending')}
              </p>
            )}
            <Button
              className="w-full mt-4"
              onClick={() => openPurchaseDialog()}
            >
              <Plus size={14} className="mr-1" />
              {t('myLicense.buyTokens')}
            </Button>
          </CardContent>
        </Card>

        {/* Event Tiers Reference */}
        <Card className="sm:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">
              {t('myLicense.tokenCosts')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {eventTiers.map(tier => (
                <div
                  key={tier.slug}
                  className={`rounded-lg border p-2 md:p-3 text-center cursor-pointer hover:border-primary/50 transition-colors ${
                    tier.slug === 'competition' ? 'border-primary/30 bg-primary/5' : ''
                  }`}
                  onClick={() => openPurchaseDialog(tier.token_cost)}
                >
                  <div className="text-xl md:text-2xl font-bold">{tier.token_cost}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">{t('myLicense.tokens')}</div>
                  <div className="text-xs md:text-sm font-medium mt-1">{tier.name}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground hidden sm:block">
                    {t('myLicense.upToLanes', { count: tier.max_lanes })} · {t('myLicense.upToDays', { count: tier.max_days })}
                  </div>
                  {tier.slug === 'competition' && (
                    <Badge className="bg-primary/15 text-primary border-primary/30 text-[10px] md:text-xs px-1 md:px-1.5 py-0 mt-1.5">
                      <Sparkles size={10} className="mr-0.5" />
                      {t('myLicense.popular')}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            {/* Pricing tiers */}
            <div className="flex gap-2 md:gap-3 mt-3 text-[10px] md:text-xs text-muted-foreground">
              {pricingTiers.map((pt, i) => (
                <div key={i} className="flex-1 text-center">
                  <span className="font-medium text-foreground">{formatPrice(pt.price_per_token_cents)}</span>
                  <span>/{t('myLicense.token')}</span>
                  <div>
                    {pt.min_quantity}-{pt.max_quantity || '∞'} {t('myLicense.tokens')}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Licenses */}
      {activeLicenses.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">
              {t('myLicense.activeLicenses')} ({activeLicenses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Desktop: Table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('myLicense.event')}</TableHead>
                    <TableHead>{t('myLicense.product')}</TableHead>
                    <TableHead className="text-center">{t('myLicense.limits')}</TableHead>
                    <TableHead className="text-center">{t('myLicense.tokensUsed')}</TableHead>
                    <TableHead>{t('myLicense.date')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLicenses.map(lic => (
                    <TableRow key={lic.id}>
                      <TableCell className="font-medium text-sm">{lic.event_name || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{lic.product_name}</TableCell>
                      <TableCell className="text-center text-xs text-muted-foreground">
                        {lic.max_lanes}L · {lic.max_days}j
                      </TableCell>
                      <TableCell className="text-center">
                        {lic.tokens_consumed > 0 ? (
                          <Badge variant="outline" className="text-xs">{lic.tokens_consumed} {t('myLicense.tokens')}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">{lic.source}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(lic.activated_at || lic.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden space-y-3">
              {activeLicenses.map(lic => (
                <div key={lic.id} className="border-b last:border-b-0 pb-3 last:pb-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{lic.event_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{lic.product_name} · {lic.max_lanes}L · {lic.max_days}j</p>
                    </div>
                    <div className="text-right shrink-0">
                      {lic.tokens_consumed > 0 ? (
                        <Badge variant="outline" className="text-xs">{lic.tokens_consumed}</Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">{lic.source}</span>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">{formatDate(lic.activated_at || lic.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide">
            {t('myLicense.transactionHistory')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              {t('myLicense.noTransactions')}
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((txn, idx) => (
                <div
                  key={txn.id}
                  className={`flex items-center justify-between py-2 px-2 -mx-2 rounded-md border-b border-border last:border-0 transition-colors duration-1000 ${
                    idx === 0 && highlightLatest ? 'bg-success/15' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {txnIcon(txn.type)}
                    <div>
                      <div className="text-sm font-medium">{txnLabel(txn.type)}</div>
                      <div className="text-xs text-muted-foreground">
                        {txn.event_name || txn.description}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${txn.tokens_amount > 0 ? 'text-success' : 'text-foreground'}`}>
                      {txn.tokens_amount > 0 ? '+' : ''}{txn.tokens_amount}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(txn.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {t('myLicense.buyTokensTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('myLicense.buyTokensDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Quantity */}
            <div>
              <Label className="text-sm">{t('myLicense.quantity')}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setQuantity(q => Math.max(1, q - 5))}
                >
                  <Minus size={14} />
                </Button>
                <NumberInput
                  min={1}
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="text-center text-lg font-bold flex-1"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setQuantity(q => q + 5)}
                >
                  <Plus size={14} />
                </Button>
              </div>
              {/* Event cost reference tooltip */}
              <div className="flex items-center gap-1 mt-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                        <Info size={12} />
                        {t('myLicense.eventCostRef')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {eventTiers.map(tier => (
                        <div key={tier.slug}>
                          <span className="font-medium">{tier.name}</span>: {tier.token_cost} {t('myLicense.tokens')}
                        </div>
                      ))}
                    </TooltipContent>
                  </Tooltip>
              </div>
            </div>

            {/* Upsell nudge */}
            {priceInfo?.next_tier_info && (
              <Alert className="bg-primary/5 border-primary/20">
                <AlertDescription className="text-xs flex items-center justify-between">
                  <span>
                    {t('myLicense.upsellNudge', {
                      tokensNeeded: priceInfo.next_tier_info.tokens_needed,
                      savings: formatPrice(priceInfo.next_tier_info.savings_per_token_cents),
                    })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-foreground ml-2"
                    onClick={() => setQuantity(quantity + priceInfo.next_tier_info!.tokens_needed)}
                  >
                    +{priceInfo.next_tier_info.tokens_needed}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Promo Code */}
            <div>
              <Label className="text-sm">{t('myLicense.promoCode')}</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={promoCode}
                  onChange={e => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoResult(null);
                  }}
                  placeholder="CODE2026"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleValidatePromo}
                  disabled={!promoCode.trim() || promoValidating}
                >
                  {promoValidating ? <Loader2 className="animate-spin" size={14} /> : <Tag size={14} />}
                  <span className="ml-1">{t('myLicense.validate')}</span>
                </Button>
              </div>
              {promoResult?.valid && (
                <p className="text-xs text-success mt-1 flex items-center gap-1">
                  <CheckCircle size={12} />
                  {promoResult.description}
                </p>
              )}
            </div>

            {/* Stripe Connect discount opt-in */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="connect-discount"
                checked={applyConnectDiscount}
                onCheckedChange={(checked) => setApplyConnectDiscount(checked === true)}
              />
              <div className="grid gap-0.5 leading-none">
                <label
                  htmlFor="connect-discount"
                  className="text-sm cursor-pointer"
                >
                  {t('myLicense.connectDiscountLabel')}
                </label>
                {applyConnectDiscount && !stripeConnectActive && (
                  <p className="text-xs text-warning flex items-center gap-1 mt-1">
                    <AlertTriangle size={12} />
                    {t('myLicense.connectDiscountReminder')}
                  </p>
                )}
                {applyConnectDiscount && stripeConnectActive && (
                  <p className="text-xs text-success flex items-center gap-1 mt-1">
                    <CheckCircle size={12} />
                    {t('myLicense.connectDiscountActive')}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            {/* Price Summary */}
            {priceLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin" size={16} />
              </div>
            ) : priceInfo ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>
                    {priceInfo.quantity} {t('myLicense.tokens')} x {formatPrice(priceInfo.price_per_token_cents)}/{t('myLicense.token')}
                  </span>
                  <span>{formatPrice(priceInfo.base_total_cents)}</span>
                </div>
                {priceInfo.discounts.map((d, i) => (
                  <div key={i} className="flex justify-between text-sm text-success">
                    <span className="flex items-center gap-1">
                      {d.type === 'stripe_connect' ? <ShieldCheck size={12} /> : <Tag size={12} />}
                      {d.label}
                    </span>
                    <span>-{formatPrice(d.amount_cents)}</span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>{t('myLicense.total')}</span>
                  <span>{formatPrice(priceInfo.final_price_cents)}</span>
                </div>
                {priceInfo.final_price_cents > 0 && (
                  <div className="text-xs text-muted-foreground text-right">
                    = {formatPrice(Math.round(priceInfo.final_price_cents / priceInfo.quantity))}/{t('myLicense.token')} {t('myLicense.afterDiscounts')}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchaseDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handlePurchase} disabled={purchasing || quantity <= 0}>
              {purchasing ? <Loader2 className="animate-spin mr-2" size={14} /> : <ExternalLink size={14} className="mr-2" />}
              {priceInfo && priceInfo.final_price_cents <= 0
                ? t('myLicense.activateFree')
                : t('myLicense.payWithStripe')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLicense;
